import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  User, 
  FileText, 
  CheckCircle, 
  Calendar, 
  Settings,
  LogOut,
  Mail,
  Phone,
  MapPin,
  Home,
  Clock,
  DollarSign,
  Loader2,
  TrendingUp,
  Quote
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import { authApi } from '../../services/authApi';
import UserQuotes from './UserQuotes';

const AccountDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await authApi.getDashboard();
      console.log('Dashboard response:', response); // Debug log
      
      // Handle the response structure properly
      if (response.data && response.data.data) {
        setDashboardData(response.data.data);
        setUser(response.data.data.user);
      } else {
        // Fallback for direct data structure
        setDashboardData(response.data);
        setUser(response.data.user || response.data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      console.error('Error details:', error.message);
      
      // Provide fallback data to prevent crashes
      const fallbackUser = authApi.getStoredUser();
      if (fallbackUser) {
        setUser(fallbackUser);
        setDashboardData({
          estimates: { all: [], pending: [], confirmed: [], total_count: 0, total_value: 0, confirmed_value: 0 },
          home_visits: [],
          appointments: [],
          summary: { total_estimates: 0, confirmed_quotes: 0, home_visits: 0, upcoming_appointments: 0 }
        });
      }
      
      toast({
        title: "Dashboard Load Error",
        description: `Failed to load dashboard data: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authApi.logout();
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully",
    });
    navigate('/');
  };

  const getStageColor = (stage) => {
    const colorMap = {
      'estimate_submitted': 'bg-[#8CB89F]/20 text-[#2F4F4F]',
      'home_visit_scheduled': 'bg-yellow-100 text-yellow-800',
      'quote_confirmed': 'bg-purple-100 text-purple-800',
      'deposit_paid_in_production': 'bg-orange-100 text-orange-800',
      'installation_scheduled': 'bg-indigo-100 text-indigo-800',
      'completed_send_review': 'bg-green-100 text-green-800',
      'completed_no_review': 'bg-gray-100 text-gray-800'
    };
    return colorMap[stage] || 'bg-gray-100 text-gray-800';
  };

  const getStageLabel = (stage) => {
    const labelMap = {
      'estimate_submitted': 'Estimate Submitted',
      'home_visit_scheduled': 'Home Visit Scheduled',
      'quote_confirmed': 'Quote Confirmed',
      'deposit_paid_in_production': 'In Production',
      'installation_scheduled': 'Installation Scheduled',
      'completed_send_review': 'Completed',
      'completed_no_review': 'Completed'
    };
    return labelMap[stage] || stage;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#8CB89F]" />
          <p className="text-lg text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-gray-600">Failed to load dashboard data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <Link to="/" className="text-[#8CB89F] hover:text-[#7aa88d] mb-2 inline-block">
              ← Back to Calculator
            </Link>
            <h1 className="text-4xl font-bold text-gray-900">My Account</h1>
            <p className="text-lg text-gray-600">Welcome back, {user?.name}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/account/settings">
              <Button variant="outline" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </Button>
            </Link>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Estimates</p>
                  <p className="text-3xl font-bold text-gray-900">{dashboardData?.summary?.total_estimates || 0}</p>
                </div>
                <div className="p-3 bg-[#8CB89F]/20 rounded-full">
                  <FileText className="h-6 w-6 text-[#8CB89F]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Confirmed Quotes</p>
                  <p className="text-3xl font-bold text-green-600">{dashboardData?.summary?.confirmed_quotes || 0}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Home Visits</p>
                  <p className="text-3xl font-bold text-orange-600">{dashboardData?.summary?.home_visits || 0}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Home className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Upcoming Appointments</p>
                  <p className="text-3xl font-bold text-purple-600">{dashboardData?.summary?.upcoming_appointments || 0}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="quotes" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              My Quotes
            </TabsTrigger>
            <TabsTrigger value="estimates" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              My Estimates
            </TabsTrigger>
            <TabsTrigger value="appointments" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Appointments
            </TabsTrigger>
            <TabsTrigger value="visits" className="flex items-center gap-2">
              <Home className="h-4 w-4" />
              Home Visits
            </TabsTrigger>
          </TabsList>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <UserQuotes user={user} />
          </TabsContent>

          {/* Estimates Tab */}
          <TabsContent value="estimates">
            <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Your Estimates</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboardData?.estimates?.all || []).length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg text-gray-600">No estimates yet</p>
                    <p className="text-sm text-gray-500 mt-2">
                      <Link to="/" className="text-[#8CB89F] hover:text-[#7aa88d]">
                        Create your first estimate
                      </Link>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(dashboardData?.estimates?.all || []).map((estimate) => (
                      <div key={estimate.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">{estimate.customer_name || 'Unknown'}</h4>
                            <Badge className={getStageColor(estimate.stage)}>
                              {getStageLabel(estimate.stage)}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-[#8CB89F]">
                              £{(estimate.total_cost || 0).toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {estimate.windows_count || 0} window{(estimate.windows_count || 0) !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>Created: {formatDate(estimate.created_at)}</span>
                          <span>Updated: {formatDate(estimate.stage_updated_at)}</span>
                        </div>
                        {estimate.notes && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
                            <strong>Notes:</strong> {estimate.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Upcoming Appointments</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboardData?.appointments || []).length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg text-gray-600">No upcoming appointments</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Your scheduled visits and installations will appear here
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(dashboardData?.appointments || []).map((appointment, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{appointment.title || 'Appointment'}</h4>
                            <p className="text-sm text-gray-600">{appointment.details || 'No details available'}</p>
                          </div>
                          <div className="text-right">
                            {appointment.date && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="h-4 w-4" />
                                <span>{formatDate(appointment.date)}</span>
                              </div>
                            )}
                            {appointment.time && (
                              <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                                <Clock className="h-4 w-4" />
                                <span>{appointment.time}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Home Visits Tab */}
          <TabsContent value="visits">
            <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Home Visit Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboardData?.home_visits || []).length === 0 ? (
                  <div className="text-center py-12">
                    <Home className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg text-gray-600">No home visit requests</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Request a free consultation through the calculator
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(dashboardData?.home_visits || []).map((visit) => (
                      <div key={visit.id} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">Home Consultation</h4>
                            <Badge className={visit.status === 'confirmed' ? 'bg-green-100 text-green-800' : 
                                           visit.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                                           'bg-gray-100 text-gray-800'}>
                              {(visit.status || 'pending').toUpperCase()}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-[#8CB89F]">
                              £{(visit.total_estimate || 0).toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {visit.windows_count || 0} window{(visit.windows_count || 0) !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>Preferred: {formatDate(visit.preferred_date)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>{visit.preferred_time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            <span>{visit.phone_number}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span>Submitted: {formatDate(visit.created_at)}</span>
                          </div>
                        </div>
                        {visit.additional_notes && (
                          <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
                            <strong>Notes:</strong> {visit.additional_notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AccountDashboard;